import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  Text,
  RichText,
  Field,
  ImageField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StCommunityContainer, StCommunityWrapper } from './CommunityList.style';
import { useRouter } from 'next/router';
import CommunityCard from '../shared/CommunityCard/CommunityCard';
import { ComponentProps } from 'lib/component-props';
import { useCallback, useEffect, useState, useRef } from 'react';
import { getQuery } from '../../lib/utils/common';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { updateCommunityExpanded, updateSelectedCommunityId } from '../../features/configSlice';
import { updateLastVisitedComponentId } from '../../features/globalConfig';
import UiHeading from '../shared/Heading';
import ButtonDiscreet from '../shared/Button/ButtonDiscreet';
import UiText from '../shared/Typography/UiText';

interface CommunityItem {
  id: string; // Add the 'id' property to the CommunityItem type
  url: string;
  name: string;
  fields: {
    title: Field<string>;
    relatedCommunity: {
      fields: { icon: ImageField };
    };
  };
}

type CommunityListProps = ComponentProps & {
  fields: {
    heading: Field<string>;
    content: Field<string>;
    viewAllCommunitiesText: Field<string>;
    listOfCommunities: CommunityItem[];
    iconColor: Field<string>;
    headingFontColor?: Field<string>;
    borderColor: Field<string>;
    borderRadius: Field<string>;
    boxShadow: Field<string>;
  };
};

// const getFieldsByName = (data: any, fieldName: any) => {
//   return data?.fields?.find((field: any) => field.name === fieldName);
// };

const CommunityList = (props: CommunityListProps): JSX.Element => {
  const { fields } = props;
  const {
    heading,
    content,
    listOfCommunities,
    viewAllCommunitiesText,
    iconColor,
    borderColor,
    borderRadius,
    boxShadow,
  } = fields;
  const crProfileData = useSelector((state: RootState) => state?.crProfile);
  const isCommunityExpanded = useSelector((state: RootState) => state?.config?.isCommunityExpanded);
  const selectedCommunityId = useSelector((state: RootState) => state?.config?.selectedCommunityId);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedCommunities = crProfileData?.selectedCommunities || [];

  const theme = useTheme();
  const dispatch = useDispatch();
  const { sitecoreContext } = useSitecoreContext();
  const isMyProfilePage = sitecoreContext?.route?.fields?.isMyProfilePage;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const router = useRouter();

  const { query } = router;
  const [filterListOfCommunity, setfilterListOfCommunity] = useState<any>([]);

  // Finding condition of website
  const siteSettings: any = sitecoreContext?.siteSettings;
  const source = siteSettings?.source?.fields?.name?.value || 'CR';

  useEffect(() => {
    if (isMyProfilePage) {
      const data = listOfCommunities.filter((item1: any) => {
        const value1 = item1.fields?.relatedCommunity?.fields?.value?.value;
        const isExist =
          selectedCommunities &&
          selectedCommunities?.length > 0 &&
          selectedCommunities?.find((item2: any) => {
            const value2 = item2.id;
            if (value1 === value2) return item2;
          });
        if (!isExist) return item1;
      });
      setfilterListOfCommunity(data);
    } else {
      setfilterListOfCommunity(listOfCommunities);
    }
  }, [listOfCommunities, selectedCommunities, router]);

  const communityContainerRef = useRef<HTMLDivElement>(null);
  const totalCommunities = filterListOfCommunity?.length;
  const MAX_COMMUNITY_NUMBER_IN_MOBILE = 5;
  const [numberOfCommunitiesToBeShown, setnumberOfCommunitiesToBeShown] = useState(0);

  const onClickHandler = (communityItem: CommunityItem) => {
    dispatch(updateLastVisitedComponentId('community-list'));
    dispatch(updateSelectedCommunityId(communityItem?.id));
    router.push(
      {
        pathname: communityItem?.url,
        query: getQuery(query),
      },
      communityItem?.url
    );
  };

  const showAllCommunities = useCallback(() => {
    const allCommunitiesElement: NodeListOf<HTMLElement> =
      document.querySelectorAll('div[role=link]');
    for (const element of allCommunitiesElement) {
      element.setAttribute('aria-hidden', 'false');
      element.classList.remove('hideElement');
    }
  }, []);

  const handleShowHideCommunityItems = useCallback(() => {
    if (sitecoreContext?.pageEditing === true || numberOfCommunitiesToBeShown === totalCommunities)
      return;

    const allCommunitiesElement: NodeListOf<HTMLElement> =
      document.querySelectorAll('div[role=link]');
    const communityContainerElement = communityContainerRef.current as HTMLDivElement;

    let visibleCommunityCount = 0;
    if (window.innerWidth < theme.breakpoints.values.sm) {
      let counter = 0;
      for (const element of allCommunitiesElement) {
        if (counter < MAX_COMMUNITY_NUMBER_IN_MOBILE) {
          counter++;
          element.setAttribute('aria-hidden', 'false');
          element.classList.remove('hideElement');
        } else {
          element.setAttribute('aria-hidden', 'true');
          element.classList.add('hideElement');
        }
      }
      setnumberOfCommunitiesToBeShown(counter);
    } else {
      const threshold =
        communityContainerElement?.offsetTop + allCommunitiesElement[0]?.offsetHeight * 2;

      for (const element of allCommunitiesElement) {
        if (element.offsetTop < threshold) {
          visibleCommunityCount++;
          element.setAttribute('aria-hidden', 'false');
          element.classList.remove('hideElement');
        } else {
          element.setAttribute('aria-hidden', 'true');
          element.classList.add('hideElement');
        }
      }
      setnumberOfCommunitiesToBeShown(visibleCommunityCount);
    }
    communityContainerElement.classList.remove('hideElement');
  }, [
    theme.breakpoints.values.sm,
    sitecoreContext?.pageEditing,
    numberOfCommunitiesToBeShown,
    totalCommunities,
  ]);

  const handleViewAllLinkClick = useCallback(() => {
    dispatch(updateCommunityExpanded(true));
    setnumberOfCommunitiesToBeShown(totalCommunities);
    showAllCommunities();
  }, [dispatch, showAllCommunities, totalCommunities]);

  useEffect(() => {
    setTimeout(() => {
      handleShowHideCommunityItems();
    }, 0);
  }, [handleShowHideCommunityItems]);

  useEffect(() => {
    window.addEventListener('resize', handleShowHideCommunityItems);
    return () => {
      window.removeEventListener('resize', handleShowHideCommunityItems);
    };
  }, [handleShowHideCommunityItems]);

  useEffect(() => {
    if (isCommunityExpanded) {
      setTimeout(() => {
        setnumberOfCommunitiesToBeShown(totalCommunities);
        showAllCommunities();
      }, 1000);
    }
  }, [showAllCommunities, isCommunityExpanded, totalCommunities]);

  if (!props?.fields) return <></>;

  if (totalCommunities < 1) {
    return <></>;
  }
  return (
    <StCommunityWrapper
      id="community-list"
      className="ui-community-list"
      sx={{
        ...(isMyProfilePage && {
          backgroundColor: theme.palette.backgroundColorType3.main,
        }),
      }}
    >
      <StCommunityContainer
        container
        item
        isDefaultApp={source !== 'CR' ? true : false}
        className="ui-community-list__container"
      >
        <Grid item data-testid="heading">
          <UiHeading size="h2" align="center">
            {heading}
          </UiHeading>
        </Grid>
        <Grid item data-testid="content" sx={{ textAlign: 'center', marginTop: '1rem' }}>
          <UiText size="body">
            <RichText field={content} />
          </UiText>
        </Grid>
        <Grid
          ref={communityContainerRef}
          id="community-list-container"
          item
          className={sitecoreContext?.pageEditing === false ? 'hideElement' : ''}
          container
          sx={{
            justifyContent: 'center',
            marginTop: '1rem',
            paddingLeft: '2rem',
            paddingRight: '2rem',
          }}
        >
          {filterListOfCommunity &&
            filterListOfCommunity.map((communityItem: CommunityItem, index: number) => (
              <CommunityCard
                key={index}
                communityItem={communityItem}
                communityCardColor={iconColor?.value}
                borderColor={borderColor?.value}
                borderRadius={borderRadius?.value}
                boxShadow={boxShadow?.value}
                isMobile={isMobile}
                onClickHandler={onClickHandler}
                onKeyUp={(e, communityItem) => {
                  if (e.key === 'Enter') {
                    onClickHandler(communityItem as any);
                  }
                }}
                role="link"
                sxCommunityCardProps={{
                  background: theme.palette.neutral0.main,
                  border:
                    selectedCommunityId === communityItem?.id
                      ? `1px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.neutral4.main}`,
                  borderRadius: '10px',
                  alignItems: 'center',
                  padding: '1rem',
                  cursor: 'pointer',
                  flexWrap: 'nowrap !important',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    background: theme.palette.secondary.dark,
                    color: theme.palette.primary.main,
                  },
                }}
                sxCommunityCardTitleProps={{
                  fontSize: '1.125rem',
                  fontWeight: 500,
                  [theme.breakpoints.down('sm')]: {
                    marginLeft: '1rem',
                  },
                  [theme.breakpoints.up('sm')]: {
                    marginTop: '0.5rem',
                  },
                }}
              />
            ))}
        </Grid>
        <Grid
          item
          sx={{ marginTop: '1rem', cursor: 'pointer', textAlign: 'center' }}
          aria-label={viewAllCommunitiesText?.value || 'View All Communities'}
        >
          {(totalCommunities !== numberOfCommunitiesToBeShown ||
            sitecoreContext?.pageEditing === true) && (
            <ButtonDiscreet onClick={handleViewAllLinkClick}>
              <Text field={viewAllCommunitiesText} />
            </ButtonDiscreet>
          )}
        </Grid>
      </StCommunityContainer>
    </StCommunityWrapper>
  );
};

export default CommunityList;
